
import { useState } from 'react';

export default function Home() {
  const [formData, setFormData] = useState({
    nome: '',
    whatsapp: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatWhatsApp = (whatsapp: string) => {
    // Remove todos os caracteres não numéricos
    const numbers = whatsapp.replace(/\D/g, '');
    
    // Se tem 10 dígitos (DDD sem 0 + número), adiciona o 0
    if (numbers.length === 10) {
      return '0' + numbers;
    }
    
    // Se tem 11 dígitos e não começa com 0, adiciona o 0
    if (numbers.length === 11 && !numbers.startsWith('0')) {
      return '0' + numbers;
    }
    
    // Retorna como está se já tem o formato correto
    return numbers;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome.trim() || !formData.whatsapp.trim()) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Formata o WhatsApp antes de enviar
      const formattedWhatsApp = formatWhatsApp(formData.whatsapp);
      
      const response = await fetch('https://readdy.ai/api/form/d3vn6fkum42jkjthsl10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          nome: formData.nome,
          whatsapp: formattedWhatsApp
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nome: '', whatsapp: '' });
        // Redirecionar para o grupo do WhatsApp após envio bem-sucedido
        setTimeout(() => {
          window.open('https://wa.me/message/DB3HIXFXNAA5J1', '_blank');
        }, 1000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = () => {
    window.open('https://wa.me/message/DB3HIXFXNAA5J1', '_blank');
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/1f08d3d511c28a7e7a957778b4430c8f/a9aa7f2cb1d9a65c16cb792278d11bc8.jpeg)'
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/70" />
      
      {/* WhatsApp Float Button */}
      <a
        href="https://wa.me/message/DB3HIXFXNAA5J1"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg transition-all duration-300 transform hover:scale-110 cursor-pointer"
      >
        <i className="fab fa-whatsapp text-2xl"></i>
      </a>
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-start px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-2xl w-full text-left">
          
          {/* Main Title */}
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-yellow-600 mb-4 sm:mb-6 font-lora tracking-wide leading-tight text-left whitespace-nowrap">
            <span className="block whitespace-nowrap">RITUAL COLETIVO</span>
          </h1>

          <div className="flex flex-col sm:flex-row items-start justify-start mb-4 sm:mb-6 px-2">
            <span className="text-red-500 font-lora text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold tracking-wider text-left leading-tight whitespace-nowrap">
              AFASTAMENTO DE RIVAL &amp; PROTEÇÃO
            </span>
          </div>

          <p className="text-yellow-600 font-lora text-lg sm:text-xl italic px-2 mb-6 sm:mb-8 text-left">
            A noite em que o véu da espiritualidade fica mais fino
          </p>

          {/* Form */}
          <form 
            id="ritual-form" 
            data-readdy-form 
            onSubmit={handleSubmit}
            className="bg-black/50 backdrop-blur-sm border border-yellow-600/30 rounded-lg p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 mx-2 sm:mx-0 mb-6 sm:mb-8"
          >
            <div>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                placeholder="Seu nome completo"
                required
                className="w-full px-3 sm:px-4 py-3 bg-black/70 border border-yellow-600/50 rounded-lg text-white placeholder-gray-300 font-lora text-base sm:text-lg focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
              />
            </div>

            <div>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleInputChange}
                placeholder="Seu WhatsApp (com DDD) - Ex: 14999887766 ou 014999887766"
                required
                className="w-full px-3 sm:px-4 py-3 bg-black/70 border border-yellow-600/50 rounded-lg text-white placeholder-gray-300 font-lora text-base sm:text-lg focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-lora font-semibold text-lg sm:text-xl py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Enviando...' : 'Quero entrar no grupo'}
            </button>

            {submitStatus === 'success' && (
              <div className="text-green-400 font-lora text-left text-sm sm:text-base">
                Inscrição realizada com sucesso! Redirecionando para o grupo...
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="text-red-400 font-lora text-left text-sm sm:text-base">
                Por favor, preencha todos os campos corretamente.
              </div>
            )}
          </form>

          <p className="text-lg sm:text-xl text-yellow-600 font-bold mb-6 sm:mb-8 leading-relaxed text-left px-2">
            ⚠️ Esse é o ÚNICO lugar onde as vagas serão abertas.
            <br />
            Entre no grupo GRATUITAMENTE e seja a primeira a receber os detalhes, os valores e GARANTIR sua vaga no ritual!
          </p>

          {/* Main Content */}
          <div className="space-y-4 sm:space-y-6 mb-8 sm:mb-10 px-2">
            <p className="text-white font-lora text-base sm:text-lg leading-relaxed text-left">
              Você já percebeu que não importa o que você faz, sempre tem alguém que aparece, comenta, insiste, manda mensagem, ou simplesmente fica rondando e atrapalhando a vida de vocês?
            </p>

            <p className="text-white font-lora text-base sm:text-lg leading-relaxed text-left">
              Você está cansada de estar sempre alerta e com medo de perdê-lo? Ou de ele encontrar outra pessoa que de mansinho preencha o espaço que é seu?
            </p>

            {/* Date Section */}
            <div className="mb-6 sm:mb-8 px-2">
              <div className="flex items-center justify-start mb-3">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-lora font-semibold text-red-500 text-left leading-tight whitespace-nowrap">
                  Dia 31 — Dia das Bruxas + Lua Azul
                </h2>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
